<template>
    <BaseSelect
        class="input-building-occupation w-full"
        :model-value="modelValue"
        :input-id="inputId"
        :options="getOccupationOptionGroups"
        option-label="label"
        option-value="value"
        option-group-label="label"
        option-group-children="items"
        :placeholder="t('placeholder')"
        @update:model-value="onUpdateValue"
        :invalid="invalid"
        :disabled="disabled"
    />
</template>

<script setup lang="ts">
// ================================
// IMPORTS
// ================================
import { computed } from "vue"
import { useI18n } from "vue-i18n"

// ================================
// INTERNATIONALIZATION
// ================================
const { t } = useI18n({
    messages: {
        fr: {
            placeholder: "Choisir un usage",
        },
    },
})

// ================================
// TYPES & INTERFACES
// ================================
interface OccupationOption {
    label: string
    value: string
}

/** Groupe d’options d’occupation (catégorie + items) pour BaseSelect */
interface OccupationOptionGroup {
    label: string
    items: OccupationOption[]
}

export interface Props {
    /**
     * v-model : valeur sélectionnée (identifiant du type d'occupation)
     */
    modelValue?: string
    /**
     * Filtre : n'afficher que les options dont la value est dans ce tableau
     */
    only?: string[]
    /**
     * Identifiant du champ
     */
    inputId?: string
    /**
     * Indique si le champ est en erreur
     */
    invalid?: boolean
    /**
     * Indique si le champ est désactivé
     */
    disabled?: boolean
}

interface Emits {
    (e: "update:modelValue", value: string): void
}

// ================================
// PROPS & EMITS
// ================================
const props = withDefaults(defineProps<Props>(), {
    modelValue: "",
    only: () => [],
    inputId: "building-occupation",
    invalid: false,
    disabled: false,
})

const emit = defineEmits<Emits>()

// ================================
// OPTIONS MÉTIER (groupes par catégorie)
// ================================
const OCCUPATION_OPTION_GROUPS: OccupationOptionGroup[] = [
    {
        label: "Résidentiel",
        items: [
            { value: "maison_individuelle", label: "Maison individuelle" },
            {
                value: "immeuble_habitation_prive",
                label: "Immeuble d'habitation privé",
            },
            { value: "logements_sociaux", label: "Logements sociaux" },
            {
                value: "residences",
                label: "Résidences étudiantes, seniors et foyers",
            },
        ],
    },
    {
        label: "Commerce et vente",
        items: [
            {
                value: "magasins",
                label: "Magasins, agences et points de vente",
            },
            { value: "centres_commerciaux", label: "Centres commerciaux" },
            { value: "restaurants_bars", label: "Restaurants, bars et cafés" },
            { value: "supermarches", label: "Supermarchés" },
        ],
    },
    {
        label: "Bureaux",
        items: [
            { value: "immeubles_bureaux", label: "Immeubles de bureaux" },
            {
                value: "centres_affaires",
                label: "Centres d'affaires et espaces de coworking",
            },
        ],
    },
    {
        label: "Industrie et logistique",
        items: [
            { value: "usines", label: "Usines et locaux industriels" },
            {
                value: "entrepots",
                label: "Entrepôts / Centres de distribution",
            },
        ],
    },
    {
        label: "Éducation",
        items: [
            { value: "creches", label: "Crèches" },
            {
                value: "ecoles",
                label: "Ecoles et établissements du 2nd degré",
            },
            {
                value: "universites",
                label: "Centres de formation et Universités",
            },
        ],
    },
    {
        label: "Santé et médico-social",
        items: [
            { value: "hopitaux", label: "Hôpitaux et cliniques" },
            { value: "ehpad", label: "EHPAD" },
        ],
    },
    {
        label: "Sport et autres",
        items: [
            { value: "salles_sport", label: "Salles de sport et piscines" },
            { value: "parkings", label: "Parkings" },
        ],
    },
]

/**
 * Groupes d’options d’occupation (filtrés par prop only si renseigné)
 */
const getOccupationOptionGroups = computed<OccupationOptionGroup[]>(() => {
    if (props.only.length === 0) {
        return OCCUPATION_OPTION_GROUPS
    }
    return OCCUPATION_OPTION_GROUPS.map((group) => ({
        label: group.label,
        items: group.items.filter((o) => props.only.includes(o.value)),
    })).filter((group) => group.items.length > 0)
})

/**
 * Émet la valeur sélectionnée (compatibilité v-model et update:value)
 */
function onUpdateValue(value: string): void {
    emit("update:modelValue", value)
}
</script>
